import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Button,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Collapse,
    Skeleton,
    type SxProps,
    type Theme,
} from '@mui/material';
import { Mail, Lock, Person, Visibility, VisibilityOff } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { validate } from '../../utils/validators';
import { CustomPhoneTextField } from '../TextInput/CustomPhoneTextField';
import type { CountryData } from 'react-phone-input-2';
import type { BuzzGuestPlatformId, InfluencerOAuthConnectedMap } from '../../utils/influencerPrelink';
import { InstagramGlyph, YoutubeGlyph, TiktokGlyph, PLATFORM_BRAND } from './SocialPlatformSvgs';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { formatSocialHandle } from '../../utils/socialHandle';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { resolveInstagramConnectProvider } from '../../utils/instagramOAuthProvider';
import { buzzBridgeProfileSelector } from '../../redux/buzzbridge/selectors';
import { useAuth } from '../../module/AuthContext';
import { ManualPlatformLinkForm } from './ManualPlatformLinkForm';
import { DevAutofillButton } from './DevAutofillButton';
import { getDevGuestSignupFields, getDevManualPlatformLinkData, isDevAutofillEnabled } from '../../utils/devAutofill';
import {
    EMPTY_MANUAL_PLATFORM_LINK,
    isManualPlatformLinkComplete,
    manualPlatformLinkFromStats,
    type IManualPlatformLinkData,
} from '../../utils/manualPlatformLinkUtils';
import { resolveInfluencerDisplayAvatarUrl } from '../../utils/influencerSocialAvatar';
import {
    type CreatorSocialPlatformId,
    influencerPlatformManualLabels,
    isCreatorPlatformLockedForGuest,
    isInfluencerPlatformAutoLinkEnabled,
} from '../../utils/influencerSocialAutoLink';
import {
    formatOauthPlatformLabel,
    formatOauthPlatformList,
    type SyncOauthPlatformId,
} from '../../utils/syncUploadCampaign';
import { OBSIDIAN } from '../../constants/obsidianDashboardTheme';
export type IInstagramManualData = IManualPlatformLinkData;
export { EMPTY_MANUAL_PLATFORM_LINK as emptyInstagramManualData };

type OAuthProvider = 'meta' | 'instagram' | 'tiktok' | 'youtube';

interface Props {
    /** Full-page vs dialog: modal drops 100vh layout and fixed decorative layers */
    variant?: 'page' | 'modal';
    /** Guest apply (OAuth + signup) vs logged-in user connecting first creator profile */
    flowType?: 'guest' | 'logged_in_connect';
    step?: number;
    totalSteps?: number;
    verificationBrandName?: string;
    showAccountFields?: boolean;
    firstName?: string;
    lastName?: string;
    onFirstNameChange?: (v: string) => void;
    onLastNameChange?: (v: string) => void;
    email?: string;
    password?: string;
    onEmailChange?: (v: string) => void;
    onPasswordChange?: (v: string) => void;
    phone?: string;
    onPhoneChange?: (v: string) => void;
    oauthConnected: InfluencerOAuthConnectedMap;
    /** True while fetching already-connected platforms from the backend on dialog open */
    loadingPlatforms?: boolean;
    connectingPlatform: BuzzGuestPlatformId | null;
    onOAuthConnecting: (platform: BuzzGuestPlatformId) => void;
    onOAuthCode: (code: string, provider: OAuthProvider, platform: BuzzGuestPlatformId) => Promise<void>;
    onOAuthFailed: (platform: BuzzGuestPlatformId) => void;
    onSubmit: () => void;
    submitBusy?: boolean;
    /** When true, primary CTA is disabled (e.g. no OAuth completed yet) */
    disableSubmit?: boolean;
    submitLabel?: string;
    onBack?: () => void;
    /** Guest flow: TikTok/YouTube "Log in" opens sign-in (e.g. close apply modal + auth modal) */
    onGuestLogInPress?: () => void;
    /** Manual Instagram entry (used when enableAutoLink.Instagram is off) */
    instagramManualData?: IInstagramManualData;
    onInstagramManualDataChange?: (data: IInstagramManualData) => void;
    /** Called when the logged-in user saves manual stats for any platform */
    onManualPlatformSave?: (platform: CreatorSocialPlatformId, data: IManualPlatformLinkData) => Promise<void>;
    /**
     * Force OAuth auto-link (e.g. sync_upload campaigns). Disables manual username entry.
     */
    requireOauthAutoLink?: boolean;
    /**
     * Platforms that must be OAuth-linked for this campaign (e.g. sync platforms or premium missing).
     * Used to label what's still needed in the connect UI.
     */
    requiredOauthPlatforms?: SyncOauthPlatformId[];
}

/** Platforms temporarily disabled from OAuth connection on the frontend */
export const LOCKED_PLATFORMS: BuzzGuestPlatformId[] = [];

const PLATFORMS_BASE: {
    id: BuzzGuestPlatformId;
    label: string;
    Glyph: React.FC<{ sx?: SxProps<Theme>; color?: string }>;
    idleColor: string;
}[] = [
    { id: 'instagram', label: 'Instagram', Glyph: InstagramGlyph, idleColor: '#b8b8b8' },
    { id: 'tiktok', label: 'TikTok', Glyph: TiktokGlyph, idleColor: '#b8b8b8' },
    { id: 'youtube', label: 'YouTube', Glyph: YoutubeGlyph, idleColor: '#b8b8b8' },
];

export const CampaignApplicationForm: React.FC<Props> = ({
    variant = 'page',
    flowType = 'guest',
    step = 1,
    totalSteps = 4,
    verificationBrandName,
    showAccountFields,
    firstName = '',
    lastName = '',
    onFirstNameChange,
    onLastNameChange,
    email = '',
    password = '',
    onEmailChange,
    onPasswordChange,
    phone = '',
    onPhoneChange,
    oauthConnected,
    loadingPlatforms = false,
    connectingPlatform,
    onOAuthConnecting,
    onOAuthCode,
    onOAuthFailed,
    onSubmit,
    submitBusy,
    disableSubmit,
    submitLabel,
    onBack,
    onGuestLogInPress,
    instagramManualData,
    onInstagramManualDataChange,
    onManualPlatformSave,
    requireOauthAutoLink = false,
    requiredOauthPlatforms,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const PLATFORMS = useMemo(() => {
        const igProvider = resolveInstagramConnectProvider(buzzBridgeConfig);
        return PLATFORMS_BASE.map(p => ({
            ...p,
            oauthProvider: (p.id === 'instagram' ? igProvider : p.id) as OAuthProvider,
        }));
    }, [buzzBridgeConfig]);
    const buzzProfile = useSelector(buzzBridgeProfileSelector);
    const { isSignedIn } = useAuth();
    const platformAutoLinkEnabled = useCallback(
        (platform: CreatorSocialPlatformId) =>
            requireOauthAutoLink || isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, platform),
        [buzzBridgeConfig, requireOauthAutoLink],
    );
    const instagramAutoLinkEnabled = platformAutoLinkEnabled('instagram');
    const profileHasInstagram = Boolean(buzzProfile?.data?.influencer?.instagram?.username);
    const existingHeadshotUrl = resolveInfluencerDisplayAvatarUrl(buzzProfile?.data?.influencer);
    const influencer = buzzProfile?.data?.influencer;
    const requiredPlatformsRaw = requiredOauthPlatforms ?? [];
    /** Logged-out guests get the usual Instagram-first signup flow — multi-platform OAuth gates apply after sign-in. */
    const isGuestLoggedOut = flowType === 'guest' && !isSignedIn;
    const requiredPlatforms = isGuestLoggedOut ? [] : requiredPlatformsRaw;
    const enforceRequiredOauth = !isGuestLoggedOut && (requireOauthAutoLink || requiredPlatforms.length > 0);
    const isOauthReady = useCallback(
        (platform: BuzzGuestPlatformId) =>
            Boolean(oauthConnected[platform]) ||
            (influencer?.[platform] as { linked?: boolean } | undefined)?.linked === true,
        [oauthConnected, influencer],
    );
    const missingOauthPlatforms = requiredPlatforms.filter((p: BuzzGuestPlatformId) => !isOauthReady(p));
    const [manualEntryOpen, setManualEntryOpen] = useState<CreatorSocialPlatformId | null>(null);
    const [manualSaveBusy, setManualSaveBusy] = useState(false);
    const [manualDrafts, setManualDrafts] = useState<Partial<Record<CreatorSocialPlatformId, IManualPlatformLinkData>>>(
        {},
    );

    const manualDataFor = useCallback(
        (platform: CreatorSocialPlatformId): IManualPlatformLinkData => {
            if (platform === 'instagram' && instagramManualData) return instagramManualData;
            if (manualDrafts[platform]) return manualDrafts[platform]!;
            const stats = influencer?.[platform];
            if (stats?.username) return manualPlatformLinkFromStats(stats);
            return { ...EMPTY_MANUAL_PLATFORM_LINK };
        },
        [instagramManualData, manualDrafts, influencer],
    );

    const isPlatformManualFilled = useCallback(
        (platform: CreatorSocialPlatformId) =>
            isManualPlatformLinkComplete(manualDataFor(platform), { existingHeadshotUrl }),
        [manualDataFor, existingHeadshotUrl],
    );

    const manualFilled = isPlatformManualFilled('instagram');
    const instagramReady = instagramAutoLinkEnabled
        ? Boolean(oauthConnected.instagram)
        : (manualFilled || profileHasInstagram) && manualEntryOpen !== 'instagram';

    const anyManualMode =
        !requireOauthAutoLink &&
        (!platformAutoLinkEnabled('instagram') ||
            !platformAutoLinkEnabled('tiktok') ||
            !platformAutoLinkEnabled('youtube'));
    const [showPw, setShowPw] = useState(false);
    const [countryCode, setCountryCode] = useState<CountryData>();
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [phoneFocused, setPhoneFocused] = useState(false);
    const [pwValid, setPwValid] = useState({ length: false, caseOk: false, number: false });
    const accountFieldsRef = useRef<HTMLDivElement>(null);
    const [scrollToAccountAfterManual, setScrollToAccountAfterManual] = useState(false);

    const handlePasswordChange = useCallback(
        (val: string) => {
            onPasswordChange?.(val);
            setPwValid({
                length: validate.validatePasswordLength(val),
                caseOk: validate.validatePasswordCase(val),
                number: validate.validatePasswordNumber(val),
            });
        },
        [onPasswordChange],
    );

    const passwordRequirements = [
        { task: '8 characters', ok: pwValid.length },
        { task: '1 uppercase & lowercase', ok: pwValid.caseOk },
        { task: '1 number', ok: pwValid.number },
    ];

    const isPasswordValid = pwValid.length && pwValid.caseOk && pwValid.number;
    const isFormValid = validate.validateEmail(email) && validate.validatePhoneNumber(phone) && isPasswordValid;

    const subtitle =
        flowType === 'logged_in_connect'
            ? requireOauthAutoLink || requiredPlatforms.length
                ? missingOauthPlatforms.length
                    ? `This campaign requires OAuth auto-linked accounts. Still needed: ${formatOauthPlatformList(
                          missingOauthPlatforms,
                      )}.`
                    : requiredPlatforms.length
                    ? `All required accounts (${formatOauthPlatformList(requiredPlatforms)}) are auto-linked.`
                    : 'This sync publish campaign requires OAuth auto-linked accounts. Connect Instagram, TikTok, or YouTube to continue.'
                : 'Connect Instagram, TikTok, or YouTube with OAuth, or enter stats manually when OAuth is off.'
            : !isSignedIn && !instagramAutoLinkEnabled
            ? 'Enter your Instagram details manually. TikTok and YouTube unlock after you log in.'
            : !isSignedIn
            ? 'Connect Instagram to continue. TikTok and YouTube are available after you log in.'
            : !instagramAutoLinkEnabled
            ? 'Select a platform below and enter your stats manually.'
            : verificationBrandName
            ? `Connect Instagram with OAuth for ${verificationBrandName} to continue.`
            : 'Connect Instagram with OAuth to continue.';

    const isModal = variant === 'modal';
    const defaultSubmitLabel = flowType === 'logged_in_connect' ? 'Apply to campaign' : 'Continue to create account';

    const platformConnectedStyle = (id: BuzzGuestPlatformId) => {
        const connected = Boolean(oauthConnected[id]);
        if (!connected) {
            return {
                bgcolor: t.deep_grey_lighter,
                border: `1px solid ${t.grey}18`,
                iconBg: t.wrapperColor,
                iconColor: t.true_white,
            };
        }
        if (id === 'instagram') {
            return {
                bgcolor: `${PLATFORM_BRAND.instagram.primary}14`,
                border: `1px solid ${PLATFORM_BRAND.instagram.primary}88`,
                iconBg: `${PLATFORM_BRAND.instagram.primary}22`,
                iconColor: PLATFORM_BRAND.instagram.primary,
            };
        }
        if (id === 'youtube') {
            return {
                bgcolor: `${PLATFORM_BRAND.youtube.primary}14`,
                border: `1px solid ${PLATFORM_BRAND.youtube.primary}88`,
                iconBg: `${PLATFORM_BRAND.youtube.primary}22`,
                iconColor: PLATFORM_BRAND.youtube.primary,
            };
        }
        return {
            bgcolor: `${PLATFORM_BRAND.tiktok.primary}12`,
            border: `1px solid ${PLATFORM_BRAND.tiktok.accent}66`,
            iconBg: `linear-gradient(135deg, ${PLATFORM_BRAND.tiktok.accent}22, ${PLATFORM_BRAND.tiktok.primary}22)`,
            iconColor: PLATFORM_BRAND.tiktok.primary,
        };
    };

    const fieldLabelSx = {
        fontSize: 10,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.15em',
        fontWeight: 700,
        color: t.grey,
        mb: 1,
        ml: 0.5,
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            height: 56,
            pl: 1,
            bgcolor: t.wrapperColor,
            borderRadius: 3,
            color: t.true_white,
            '& fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: `1px solid ${t.highlight}66` },
        },
    };

    const creatorCanApply = instagramAutoLinkEnabled ? Object.keys(oauthConnected).length > 0 : profileHasInstagram;
    const formDisabled = flowType === 'guest' ? !isFormValid || disableSubmit : !creatorCanApply;

    const completeManualPlatformEntry = useCallback(async () => {
        if (!manualEntryOpen) return;
        const platform = manualEntryOpen;
        const data = manualDataFor(platform);
        if (!isManualPlatformLinkComplete(data, { existingHeadshotUrl })) return;

        if (platform === 'instagram') {
            onInstagramManualDataChange?.(data);
        }

        if (flowType === 'guest' && platform === 'instagram') {
            setManualEntryOpen(null);
            if (showAccountFields) setScrollToAccountAfterManual(true);
            return;
        }

        if (onManualPlatformSave) {
            setManualSaveBusy(true);
            try {
                await onManualPlatformSave(platform, data);
            } catch {
                return;
            } finally {
                setManualSaveBusy(false);
            }
        }
        setManualEntryOpen(null);
    }, [
        manualEntryOpen,
        manualDataFor,
        existingHeadshotUrl,
        flowType,
        showAccountFields,
        onInstagramManualDataChange,
        onManualPlatformSave,
    ]);

    const handleDevAutofill = useCallback(() => {
        if (!isDevAutofillEnabled()) return;

        if (!instagramAutoLinkEnabled) {
            onInstagramManualDataChange?.(getDevManualPlatformLinkData());
            setManualEntryOpen(null);
            if (flowType === 'guest' && showAccountFields) {
                setScrollToAccountAfterManual(true);
            }
        }

        if (flowType === 'guest' && showAccountFields && !isSignedIn) {
            const signup = getDevGuestSignupFields();
            onFirstNameChange?.(signup.firstName);
            onLastNameChange?.(signup.lastName);
            onEmailChange?.(signup.email);
            onPasswordChange?.(signup.password);
            onPhoneChange?.(signup.phone);
            setPwValid({ length: true, caseOk: true, number: true });
        }
    }, [
        flowType,
        showAccountFields,
        isSignedIn,
        instagramAutoLinkEnabled,
        onInstagramManualDataChange,
        onFirstNameChange,
        onLastNameChange,
        onEmailChange,
        onPasswordChange,
        onPhoneChange,
    ]);

    useEffect(() => {
        if (!scrollToAccountAfterManual || !instagramReady) return;

        const scrollTimer = window.setTimeout(() => {
            accountFieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setScrollToAccountAfterManual(false);
        }, 650);

        return () => window.clearTimeout(scrollTimer);
    }, [scrollToAccountAfterManual, instagramReady]);

    const submitSection = (
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <Button
                onClick={onSubmit}
                disabled={submitBusy || formDisabled}
                fullWidth
                sx={{
                    py: 2.5,
                    borderRadius: 10,
                    background: `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange})`,
                    color: t.button_text,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontWeight: 800,
                    fontSize: 18,
                    textTransform: 'none',
                    boxShadow: `0 10px 30px ${t.highlight}30`,
                    '&:hover': { boxShadow: `0 15px 40px ${t.highlight}45` },
                    '&:active': { transform: 'scale(0.95)' },
                    transition: 'all 0.3s',
                }}>
                {submitBusy ? (
                    <CircularProgress size={24} sx={{ color: t.button_text }} />
                ) : (
                    submitLabel ?? defaultSubmitLabel
                )}
            </Button>
            {flowType === 'guest' && onGuestLogInPress && (
                <Typography
                    sx={{
                        fontSize: 14,
                        color: t.subtle_white,
                        textAlign: 'center',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    Already have an account?{' '}
                    <Box
                        component="button"
                        type="button"
                        onClick={onGuestLogInPress}
                        sx={{
                            border: 'none',
                            p: 0,
                            bgcolor: 'transparent',
                            color: t.highlight,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textUnderlineOffset: 3,
                            '&:hover': { opacity: 0.85 },
                        }}>
                        Sign in
                    </Box>
                </Typography>
            )}
            <Typography
                sx={{
                    fontSize: 12,
                    color: t.grey,
                    textAlign: 'center',
                    maxWidth: 400,
                    lineHeight: 1.6,
                }}>
                {flowType === 'guest'
                    ? 'By continuing, you agree to our Editorial Standards and Creator Licensing Agreement. Social sign-in uses the same secure OAuth flow as the rest of the app.'
                    : 'We only use OAuth to verify your channels. You can manage connected accounts from your creator profile after applying.'}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: isModal ? 0 : '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: isModal ? 'flex-start' : 'center',
                px: isModal ? 0 : 3,
                py: isModal ? 0 : { xs: 6, md: 12 },
                position: 'relative',
                overflow: isModal ? 'visible' : undefined,
            }}>
            {onBack && !isModal && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: 16, md: 24 },
                        left: { xs: 16, md: 24 },
                        zIndex: 2,
                    }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            color: t.highlight,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                        }}>
                        Back
                    </Button>
                </Box>
            )}

            {onBack && isModal && (
                <Box sx={{ alignSelf: 'stretch', mb: 2, zIndex: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            color: t.highlight,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                        }}>
                        Back
                    </Button>
                </Box>
            )}

            {!isModal && (
                <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '-10%',
                            right: '-10%',
                            width: 500,
                            height: 500,
                            bgcolor: `${t.highlight}08`,
                            borderRadius: '50%',
                            filter: 'blur(120px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '-10%',
                            left: '-10%',
                            width: 400,
                            height: 400,
                            bgcolor: `${t.btnCustomColor}08`,
                            borderRadius: '50%',
                            filter: 'blur(100px)',
                        }}
                    />
                </Box>
            )}

            <Box sx={{ width: '100%', maxWidth: 560, zIndex: 1 }}>
                <Box sx={{ mb: isModal ? 4 : 6, textAlign: { xs: 'center', md: 'left' } }}>
                    <Chip
                        icon={
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: t.btnCustomColor,
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.4 },
                                    },
                                }}
                            />
                        }
                        label={`Step ${String(step).padStart(2, '0')} / ${String(totalSteps).padStart(2, '0')}`}
                        sx={{
                            bgcolor: t.wrapperColor,
                            color: t.btnCustomColor,
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            mb: 2,
                            px: 1,
                        }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                        <Typography
                            sx={{
                                flex: 1,
                                fontSize: { xs: 32, md: 44 },
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                letterSpacing: '-0.03em',
                                color: t.true_white,
                            }}>
                            {flowType === 'logged_in_connect' ? 'Connect your socials' : 'Start Your Application'}
                        </Typography>
                        <DevAutofillButton
                            onAutofill={handleDevAutofill}
                            title={
                                flowType === 'guest'
                                    ? 'Dev: fill Instagram stats + signup'
                                    : 'Dev: fill manual Instagram stats'
                            }
                            color={t.highlight}
                        />
                    </Box>
                    <Typography
                        sx={{
                            color: t.subtle_white,
                            fontSize: 18,
                            maxWidth: 480,
                            lineHeight: 1.5,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {subtitle}
                    </Typography>
                    {!isGuestLoggedOut && missingOauthPlatforms.length > 0 ? (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                borderRadius: '10px',
                                bgcolor: 'rgba(255,201,101,0.08)',
                                border: '1px solid rgba(255,201,101,0.28)',
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: OBSIDIAN.primary,
                                    fontWeight: 700,
                                    mb: 0.75,
                                }}>
                                Missing auto-link
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {missingOauthPlatforms.map(id => (
                                    <Chip
                                        key={id}
                                        size="small"
                                        label={formatOauthPlatformLabel(id)}
                                        sx={{
                                            height: 26,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontWeight: 600,
                                            fontSize: 12,
                                            bgcolor: 'rgba(255,201,101,0.12)',
                                            color: OBSIDIAN.primary,
                                            border: '1px solid rgba(255,201,101,0.35)',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Box sx={{ height: 1, flex: 1, bgcolor: t.wrapperColor }} />
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.15em',
                                    color: t.grey,
                                }}>
                                {manualEntryOpen
                                    ? 'Connect manually'
                                    : anyManualMode
                                    ? 'Link your platforms'
                                    : 'Connect with OAuth'}
                            </Typography>
                            <Box sx={{ height: 1, flex: 1, bgcolor: t.wrapperColor }} />
                        </Box>

                        {manualEntryOpen && !platformAutoLinkEnabled(manualEntryOpen) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => setManualEntryOpen(null)}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        color: t.subtle_white,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        p: 0,
                                        mb: 0.5,
                                        '&:hover': { bgcolor: 'transparent', color: t.true_white },
                                    }}>
                                    Back
                                </Button>
                                <ManualPlatformLinkForm
                                    value={manualDataFor(manualEntryOpen)}
                                    onChange={data => {
                                        if (manualEntryOpen === 'instagram') {
                                            onInstagramManualDataChange?.(data);
                                        } else {
                                            setManualDrafts(prev => ({ ...prev, [manualEntryOpen]: data }));
                                        }
                                    }}
                                    showAutoLinkUnavailableNotice
                                    existingHeadshotUrl={existingHeadshotUrl}
                                    {...influencerPlatformManualLabels(manualEntryOpen)}
                                    textFieldSx={textFieldSx}
                                    fieldLabelSx={fieldLabelSx}
                                    mutedColor={t.grey}
                                    highlightColor={t.highlight}
                                />
                                <Button
                                    fullWidth
                                    disabled={!isPlatformManualFilled(manualEntryOpen) || manualSaveBusy}
                                    onClick={() => void completeManualPlatformEntry()}
                                    sx={{
                                        mt: 1,
                                        py: 2.5,
                                        borderRadius: 10,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: 18,
                                        color: t.button_text,
                                        background: `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange})`,
                                        boxShadow: `0 10px 30px ${t.highlight}30`,
                                        '&:hover': { boxShadow: `0 15px 40px ${t.highlight}45` },
                                        '&:active': { transform: 'scale(0.95)' },
                                        transition: 'all 0.3s',
                                        '&.Mui-disabled': { opacity: 0.4 },
                                    }}>
                                    {manualSaveBusy ? (
                                        <CircularProgress size={24} sx={{ color: t.button_text }} />
                                    ) : (
                                        'Save'
                                    )}
                                </Button>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                    gap: 2,
                                }}>
                                {loadingPlatforms
                                    ? PLATFORMS.map(p => (
                                          <Box
                                              key={p.id}
                                              sx={{
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'center',
                                                  p: 3,
                                                  borderRadius: 3,
                                                  border: `1px solid ${t.grey}18`,
                                                  bgcolor: t.deep_grey_lighter,
                                                  gap: 1.5,
                                              }}>
                                              <Skeleton
                                                  variant="circular"
                                                  width={48}
                                                  height={48}
                                                  sx={{ bgcolor: t.wrapperColor }}
                                              />
                                              <Skeleton
                                                  variant="text"
                                                  width={72}
                                                  height={20}
                                                  sx={{ bgcolor: t.wrapperColor }}
                                              />
                                              <Skeleton
                                                  variant="rounded"
                                                  width="100%"
                                                  height={40}
                                                  sx={{ bgcolor: t.wrapperColor, borderRadius: 2 }}
                                              />
                                          </Box>
                                      ))
                                    : PLATFORMS.map(p => {
                                          const st = platformConnectedStyle(p.id);
                                          const profileConnected = Boolean(influencer?.[p.id]?.username);
                                          const oauthReady = isOauthReady(p.id);
                                          const isRequiredPlatform =
                                              !requiredPlatforms.length || requiredPlatforms.includes(p.id);
                                          const needsOauth = enforceRequiredOauth && isRequiredPlatform && !oauthReady;
                                          // Required OAuth platforms need a real auto-link; others can show username.
                                          const connected = enforceRequiredOauth
                                              ? oauthReady
                                              : Boolean(oauthConnected[p.id]) || profileConnected;
                                          const busyHere = connectingPlatform === p.id;
                                          const platformLocked = LOCKED_PLATFORMS.includes(p.id);
                                          const guestLockedNonInstagram =
                                              isCreatorPlatformLockedForGuest(p.id, isSignedIn, connected) ||
                                              (platformLocked && !connected);
                                          const isManualMode = !platformAutoLinkEnabled(p.id);
                                          const platformManualFilled = isPlatformManualFilled(p.id);

                                          const connectButton = (
                                              <Button
                                                  fullWidth
                                                  disabled={Boolean(connectingPlatform && connectingPlatform !== p.id)}
                                                  sx={{
                                                      mt: 1,
                                                      py: 1.25,
                                                      borderRadius: 2,
                                                      textTransform: 'none',
                                                      fontFamily: FONT_ENUM.MAIN_BOLD,
                                                      fontSize: 13,
                                                      color: needsOauth ? OBSIDIAN.onPrimaryFixed : t.true_white,
                                                      border: needsOauth
                                                          ? '1px solid transparent'
                                                          : `1px solid ${t.grey}40`,
                                                      bgcolor: needsOauth ? OBSIDIAN.primary : t.wrapperColor,
                                                      fontWeight: 700,
                                                      '&:hover': {
                                                          bgcolor: needsOauth ? '#ffd080' : t.card_grey,
                                                      },
                                                  }}>
                                                  {busyHere ? (
                                                      <CircularProgress
                                                          size={22}
                                                          sx={{
                                                              color: needsOauth ? OBSIDIAN.onPrimaryFixed : t.highlight,
                                                          }}
                                                      />
                                                  ) : needsOauth ? (
                                                      'Auto-link required'
                                                  ) : (
                                                      'Connect'
                                                  )}
                                              </Button>
                                          );
                                          const logInButton = (
                                              <Button
                                                  fullWidth
                                                  onClick={() => onGuestLogInPress?.()}
                                                  disabled={!onGuestLogInPress}
                                                  sx={{
                                                      mt: 1,
                                                      py: 1.25,
                                                      borderRadius: 2,
                                                      textTransform: 'none',
                                                      fontFamily: FONT_ENUM.MAIN_BOLD,
                                                      fontSize: 13,
                                                      color: t.subtle_white ?? t.grey,
                                                      border: `1px solid ${t.grey}35`,
                                                      bgcolor: t.deep_grey_lighter,
                                                      '&:hover': onGuestLogInPress
                                                          ? { bgcolor: t.card_grey, borderColor: `${t.grey}55` }
                                                          : undefined,
                                                  }}>
                                                  Log in
                                              </Button>
                                          );

                                          return (
                                              <Box
                                                  key={p.id}
                                                  sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      p: 3,
                                                      bgcolor: needsOauth ? 'rgba(255,201,101,0.06)' : st.bgcolor,
                                                      borderRadius: 3,
                                                      border: needsOauth
                                                          ? '1px solid rgba(255,201,101,0.45)'
                                                          : platformManualFilled && !connected
                                                          ? `1px solid ${st.iconColor}60`
                                                          : st.border,
                                                      transition: 'all 0.3s',
                                                      position: 'relative',
                                                  }}>
                                                  {needsOauth ? (
                                                      <Chip
                                                          size="small"
                                                          label="Missing"
                                                          sx={{
                                                              position: 'absolute',
                                                              top: 10,
                                                              right: 10,
                                                              height: 22,
                                                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                              fontWeight: 700,
                                                              fontSize: 10,
                                                              letterSpacing: '0.04em',
                                                              textTransform: 'uppercase',
                                                              bgcolor: OBSIDIAN.primary,
                                                              color: OBSIDIAN.onPrimaryFixed,
                                                          }}
                                                      />
                                                  ) : null}
                                                  <Box
                                                      className="platform-icon"
                                                      sx={{
                                                          width: 48,
                                                          height: 48,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          borderRadius: '50%',
                                                          background: needsOauth ? 'rgba(255,201,101,0.14)' : st.iconBg,
                                                          color: needsOauth ? OBSIDIAN.primary : st.iconColor,
                                                          mb: 1.5,
                                                          transition: 'all 0.3s',
                                                          position: 'relative',
                                                      }}>
                                                      <p.Glyph
                                                          color={
                                                              connected || platformManualFilled
                                                                  ? st.iconColor
                                                                  : needsOauth
                                                                  ? OBSIDIAN.primary
                                                                  : p.idleColor
                                                          }
                                                      />
                                                      {guestLockedNonInstagram && (
                                                          <Box
                                                              sx={{
                                                                  position: 'absolute',
                                                                  bottom: -2,
                                                                  right: -2,
                                                                  width: 22,
                                                                  height: 22,
                                                                  borderRadius: '50%',
                                                                  bgcolor: t.wrapperColor,
                                                                  border: `1px solid ${t.grey}40`,
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  justifyContent: 'center',
                                                              }}>
                                                              <LockOutlinedIcon sx={{ fontSize: 12, color: t.grey }} />
                                                          </Box>
                                                      )}
                                                  </Box>
                                                  <Typography
                                                      sx={{
                                                          fontSize: 14,
                                                          fontFamily: FONT_ENUM.MAIN_BOLD,
                                                          fontWeight: 700,
                                                          color: t.true_white,
                                                      }}>
                                                      {p.label}
                                                  </Typography>
                                                  {connected && (
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              gap: 0.5,
                                                              mt: 0.5,
                                                          }}>
                                                          <CheckCircleIcon sx={{ fontSize: 16, color: st.iconColor }} />
                                                          <Typography
                                                              sx={{
                                                                  fontSize: 12,
                                                                  color: st.iconColor,
                                                                  fontWeight: 600,
                                                              }}>
                                                              {oauthConnected[p.id]?.label
                                                                  ? formatSocialHandle(oauthConnected[p.id]!.label)
                                                                  : profileConnected
                                                                  ? formatSocialHandle(influencer![p.id]!.username)
                                                                  : 'Connected'}
                                                          </Typography>
                                                      </Box>
                                                  )}
                                                  {needsOauth && profileConnected && !connected ? (
                                                      <Typography
                                                          sx={{
                                                              mt: 0.5,
                                                              fontSize: 11,
                                                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                              color: 'rgba(255,255,255,0.55)',
                                                              textAlign: 'center',
                                                          }}>
                                                          {formatSocialHandle(influencer![p.id]!.username)} · username
                                                          only
                                                      </Typography>
                                                  ) : null}
                                                  {!connected && platformManualFilled && !needsOauth && (
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              gap: 0.5,
                                                              mt: 0.5,
                                                          }}>
                                                          <CheckCircleIcon sx={{ fontSize: 16, color: st.iconColor }} />
                                                          <Typography
                                                              sx={{
                                                                  fontSize: 12,
                                                                  color: st.iconColor,
                                                                  fontWeight: 600,
                                                              }}>
                                                              {formatSocialHandle(manualDataFor(p.id).userName)}
                                                          </Typography>
                                                      </Box>
                                                  )}

                                                  {!connected &&
                                                      guestLockedNonInstagram &&
                                                      (platformLocked ? (
                                                          <Button
                                                              fullWidth
                                                              disabled
                                                              sx={{
                                                                  mt: 1,
                                                                  py: 1.25,
                                                                  borderRadius: 2,
                                                                  textTransform: 'none',
                                                                  fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                  fontSize: 13,
                                                                  color: t.grey,
                                                                  border: `1px solid ${t.grey}40`,
                                                                  bgcolor: t.wrapperColor,
                                                                  '&.Mui-disabled': {
                                                                      cursor: 'not-allowed',
                                                                      pointerEvents: 'all',
                                                                  },
                                                              }}>
                                                              Connect
                                                          </Button>
                                                      ) : (
                                                          logInButton
                                                      ))}

                                                  {!connected && !guestLockedNonInstagram && isManualMode && (
                                                      <Button
                                                          fullWidth
                                                          onClick={() =>
                                                              setManualEntryOpen(prev => (prev === p.id ? null : p.id))
                                                          }
                                                          sx={{
                                                              mt: 1,
                                                              py: 1.25,
                                                              borderRadius: 2,
                                                              textTransform: 'none',
                                                              fontFamily: FONT_ENUM.MAIN_BOLD,
                                                              fontSize: 13,
                                                              color: t.true_white,
                                                              border: `1px solid ${t.grey}40`,
                                                              bgcolor: t.wrapperColor,
                                                              '&:hover': { bgcolor: t.card_grey },
                                                          }}>
                                                          {platformManualFilled ? 'Edit' : 'Connect'}
                                                      </Button>
                                                  )}

                                                  {!connected && !guestLockedNonInstagram && !isManualMode && (
                                                      <SocialLogin
                                                          provider={p.oauthProvider}
                                                          onClick={() => onOAuthConnecting(p.id)}
                                                          onCodeReceived={code =>
                                                              void onOAuthCode(code, p.oauthProvider, p.id)
                                                          }
                                                          onAuthComplete={success => {
                                                              if (!success) onOAuthFailed(p.id);
                                                          }}
                                                          customButton={connectButton}
                                                      />
                                                  )}
                                              </Box>
                                          );
                                      })}
                            </Box>
                        )}
                    </Box>

                    {showAccountFields && flowType === 'guest' && !isSignedIn && (
                        <Box ref={accountFieldsRef}>
                            <Collapse in={instagramReady} timeout={600}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    <Box
                                        sx={{
                                            p: { xs: 3, sm: 4 },
                                            bgcolor: t.deep_grey_lighter,
                                            borderRadius: '2rem',
                                            border: `1px solid ${t.grey}12`,
                                        }}>
                                        <Box
                                            sx={{
                                                mb: 3,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: 20,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: t.true_white,
                                                        mb: 0.5,
                                                    }}>
                                                    Almost there!
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, color: t.subtle_white }}>
                                                    Enter your details below so we can contact you if the brand wants to
                                                    get in touch.
                                                </Typography>
                                            </Box>
                                            <DevAutofillButton
                                                onAutofill={() => {
                                                    const signup = getDevGuestSignupFields();
                                                    onFirstNameChange?.(signup.firstName);
                                                    onLastNameChange?.(signup.lastName);
                                                    onEmailChange?.(signup.email);
                                                    onPasswordChange?.(signup.password);
                                                    onPhoneChange?.(signup.phone);
                                                    setPwValid({ length: true, caseOk: true, number: true });
                                                }}
                                                title="Dev: fill signup fields"
                                                color={t.highlight}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={fieldLabelSx}>First Name</Typography>
                                                    <TextField
                                                        fullWidth
                                                        value={firstName}
                                                        onChange={e => onFirstNameChange?.(e.target.value)}
                                                        placeholder="Jane"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Person
                                                                        sx={{ color: t.subtle_white, fontSize: 22 }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={textFieldSx}
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={fieldLabelSx}>Last Name</Typography>
                                                    <TextField
                                                        fullWidth
                                                        value={lastName}
                                                        onChange={e => onLastNameChange?.(e.target.value)}
                                                        placeholder="Doe"
                                                        sx={textFieldSx}
                                                    />
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography sx={fieldLabelSx}>Email Address</Typography>
                                                <TextField
                                                    fullWidth
                                                    type="email"
                                                    value={email}
                                                    onChange={e => {
                                                        onEmailChange?.(e.target.value);
                                                        if (emailError) setEmailError('');
                                                    }}
                                                    onBlur={() => {
                                                        if (email && !validate.validateEmail(email))
                                                            setEmailError('Enter a valid email address');
                                                        else setEmailError('');
                                                    }}
                                                    onFocus={() => setEmailError('')}
                                                    error={!!emailError}
                                                    helperText={emailError}
                                                    placeholder="creator@example.com"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Mail sx={{ color: t.subtle_white, fontSize: 22 }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        ...textFieldSx,
                                                        ...(emailError && {
                                                            '& .MuiOutlinedInput-root': {
                                                                ...textFieldSx['& .MuiOutlinedInput-root'],
                                                                '& fieldset': {
                                                                    border: `1px solid ${t.secondary_red}`,
                                                                },
                                                            },
                                                        }),
                                                        '& .MuiFormHelperText-root': {
                                                            color: t.secondary_red,
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            fontSize: 12,
                                                            ml: 0.5,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography sx={fieldLabelSx}>Phone Number</Typography>
                                                <CustomPhoneTextField
                                                    value={phone}
                                                    onChange={(val, data) => {
                                                        onPhoneChange?.(val);
                                                        setCountryCode(data);
                                                        if (phoneError) setPhoneError('');
                                                    }}
                                                    onFocus={() => {
                                                        setPhoneFocused(true);
                                                        setPhoneError('');
                                                    }}
                                                    onBlur={() => {
                                                        setPhoneFocused(false);
                                                        if (
                                                            phone &&
                                                            !validate.validatePhoneNumber(
                                                                phone,
                                                                countryCode?.countryCode,
                                                            )
                                                        )
                                                            setPhoneError(
                                                                `Invalid phone number${
                                                                    countryCode?.name ? ` for ${countryCode.name}` : ''
                                                                }`,
                                                            );
                                                        else setPhoneError('');
                                                    }}
                                                    isErrorState={!!phoneError}
                                                    errorMsg={phoneError}
                                                    isFocused={phoneFocused}
                                                    customStyle={{
                                                        height: 56,
                                                        backgroundColor: t.wrapperColor,
                                                        borderWidth: 0,
                                                        borderRadius: 12,
                                                        backgroundImage: 'none',
                                                        color: t.true_white,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        fontSize: 14,
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography sx={fieldLabelSx}>Set Password</Typography>
                                                <TextField
                                                    fullWidth
                                                    type={showPw ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={e => handlePasswordChange(e.target.value)}
                                                    placeholder="••••••••••••"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Lock sx={{ color: t.subtle_white, fontSize: 22 }} />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={() => setShowPw(s => !s)}
                                                                    sx={{ color: t.subtle_white }}>
                                                                    {showPw ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={textFieldSx}
                                                />
                                                {password && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                            mt: 1.5,
                                                            ml: 0.5,
                                                        }}>
                                                        {passwordRequirements.map(req => (
                                                            <Typography
                                                                key={req.task}
                                                                sx={{
                                                                    fontSize: 11,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: req.ok
                                                                        ? t.tertiary_green ?? '#4caf50'
                                                                        : t.grey,
                                                                    transition: 'color 0.2s',
                                                                }}>
                                                                {req.ok ? '✓' : '○'} {req.task}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    {submitSection}
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {flowType !== 'guest' && !manualEntryOpen && submitSection}
                </Box>
            </Box>
        </Box>
    );
};
